import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getGlobalSettings, updateGlobalSettings } from '../services/vpsGlobalSettings';
import './GlobalSettings.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://multi-counter.bancheng.tech/api';

const defaultSoundSettings = {
  enabled: true,
  volume: 60,
  beepCount: 3,
  pitch: 'medium',
  customSoundUrl: ''
};

function GlobalSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [soundSettings, setSoundSettings] = useState({ ...defaultSoundSettings });
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const settings = await getGlobalSettings();
      setSoundSettings({
        ...defaultSoundSettings,
        ...(settings.soundSettings || {})
      });
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      setMessage({ type: 'error', text: '載入設定失敗：' + err.message });
    } finally {
      setLoading(false);
    }
  };

  const updateSoundSetting = (key, value) => {
    setSoundSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await updateGlobalSettings({ soundSettings });
      setMessage({ type: 'success', text: '設定已儲存！' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: '儲存失敗：' + err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('檔案大小不能超過 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('sound', file);

      const response = await fetch(`${API_BASE}/uploads/sound`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('上傳失敗');
      }

      const data = await response.json();
      updateSoundSetting('customSoundUrl', data.url);
      setMessage({ type: 'success', text: '音檔上傳成功！' });
    } catch (err) {
      alert('上傳失敗：' + err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const playPreviewSound = () => {
    const url = soundSettings.customSoundUrl;
    if (!url) return;

    const audio = new Audio(url);
    audio.volume = soundSettings.volume / 100;
    audio.play().catch(err => {
      console.error('Preview failed:', err);
      alert('播放失敗，請確認音檔 URL 正確');
    });
  };

  const playDefaultBeep = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const pitchFreq = {
        low: { base: 440, alt: 523 },
        medium: { base: 880, alt: 1046 },
        high: { base: 1320, alt: 1568 }
      };
      const freqs = pitchFreq[soundSettings.pitch] || pitchFreq.medium;

      for (let i = 0; i < soundSettings.beepCount; i++) {
        const startTime = audioContext.currentTime + i * 0.25;
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.type = 'sine';
        oscillator.frequency.value = i % 2 === 0 ? freqs.base : freqs.alt;

        const vol = soundSettings.volume / 100;
        gainNode.gain.setValueAtTime(vol * 0.6, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.2);

        oscillator.start(startTime);
        oscillator.stop(startTime + 0.2);
      }
    } catch (err) {
      console.error('Beep failed:', err);
    }
  };

  if (loading) {
    return (
      <div className="global-settings-page">
        <div className="loading">載入中...</div>
      </div>
    );
  }

  return (
    <div className="global-settings-page">
      <div className="page-header">
        <button className="btn btn-back" onClick={() => navigate('/dashboard')}>
          ← 返回
        </button>
        <h1>⚙️ 全域設定</h1>
      </div>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="settings-card">
        <h2>🔊 跳階段音效預設設定</h2>
        <p className="settings-description">
          這些設定將作為系統預設值，套用於未設定音效的公司與計時器。
          <br />
          繼承順序：<strong>計時器獨立設定 → 公司設定 → 全域設定</strong>
        </p>

        <div className="form-group checkbox-group">
          <label>
            <input
              type="checkbox"
              checked={soundSettings.enabled}
              onChange={(e) => updateSoundSetting('enabled', e.target.checked)}
            />
            啟用跳階段音效
          </label>
        </div>

        {soundSettings.enabled && (
          <>
            <div className="form-group">
              <label>音量 ({soundSettings.volume}%)</label>
              <input
                type="range"
                min="0"
                max="100"
                value={soundSettings.volume}
                onChange={(e) => updateSoundSetting('volume', parseInt(e.target.value))}
                className="volume-slider"
              />
            </div>

            <div className="form-group">
              <label>嗶聲次數</label>
              <select
                value={soundSettings.beepCount}
                onChange={(e) => updateSoundSetting('beepCount', parseInt(e.target.value))}
              >
                <option value={1}>1 次</option>
                <option value={2}>2 次</option>
                <option value={3}>3 次</option>
                <option value={4}>4 次</option>
                <option value={5}>5 次</option>
              </select>
            </div>

            <div className="form-group">
              <label>音調</label>
              <select
                value={soundSettings.pitch}
                onChange={(e) => updateSoundSetting('pitch', e.target.value)}
              >
                <option value="low">低音 (440Hz)</option>
                <option value="medium">中音 (880Hz)</option>
                <option value="high">高音 (1320Hz)</option>
              </select>
            </div>

            <div className="form-group">
              <label>自訂音檔</label>
              <div className="sound-upload-section">
                <div className="upload-row">
                  <input
                    type="file"
                    accept="audio/*"
                    onChange={handleAudioUpload}
                    disabled={uploading}
                    style={{ display: 'none' }}
                    id="audio-upload-global"
                  />
                  <label htmlFor="audio-upload-global" className="btn btn-secondary upload-btn">
                    {uploading ? '上傳中...' : '📁 選擇音檔'}
                  </label>
                  {soundSettings.customSoundUrl && (
                    <>
                      <button type="button" className="btn btn-info preview-btn" onClick={playPreviewSound}>
                        🔊 試聽
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger clear-btn"
                        onClick={() => updateSoundSetting('customSoundUrl', '')}
                      >
                        ✕
                      </button>
                    </>
                  )}
                </div>
                {soundSettings.customSoundUrl && (
                  <div className="current-sound">
                    <small>目前音檔：{soundSettings.customSoundUrl.split('/').pop()}</small>
                  </div>
                )}
                <div className="url-input-row">
                  <input
                    type="url"
                    value={soundSettings.customSoundUrl}
                    onChange={(e) => updateSoundSetting('customSoundUrl', e.target.value)}
                    placeholder="或直接輸入音檔 URL"
                  />
                </div>
                <small className="help-text">支援 MP3, WAV, OGG, M4A 格式，最大 5MB。留空使用預設嗶聲</small>
              </div>
            </div>

            <div className="form-group">
              <label>預覽音效</label>
              <div className="preview-buttons">
                <button type="button" className="btn btn-secondary" onClick={playDefaultBeep}>
                  🔔 試聽預設嗶聲
                </button>
                {soundSettings.customSoundUrl && (
                  <button type="button" className="btn btn-info" onClick={playPreviewSound}>
                    🔊 試聽自訂音檔
                  </button>
                )}
              </div>
            </div>
          </>
        )}

        <div className="form-actions">
          <button
            className="btn btn-primary btn-lg"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? '儲存中...' : '💾 儲存設定'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default GlobalSettings;
